function initDropdownAutoPosition({
  rootSelector = '.dropdown',
  menuSelector = '.dropdown__menu',
  topClass = 'dropdown--position-top',
  bottomClass = 'dropdown--position-bottom',
  offset = 8,
} = {}) {
  const dropdowns = Array.from(document.querySelectorAll(rootSelector));
  if (!dropdowns.length) return;
  const hasHiddenMoreItems = (dropdown) =>
    Boolean(
      dropdown.querySelector(`${menuSelector} .dropdown__item--hidden[hidden], ${menuSelector} .dropdown__item--hidden`)
    );

  function updateDropdownPosition(dropdown) {
    const menu = dropdown.querySelector(menuSelector);
    if (!menu) return;

    const rect = dropdown.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const menuHeight = menu.scrollHeight;
    const spaceBelow = viewportHeight - rect.bottom;

    if (spaceBelow < menuHeight + offset) {
      dropdown.classList.add(topClass);
      dropdown.classList.remove(bottomClass);
    } else {
      dropdown.classList.add(bottomClass);
      dropdown.classList.remove(topClass);
    }
  }

  function updateAll() {
    dropdowns.forEach(updateDropdownPosition);
  }

  function smoothKeepMenuInView(dropdown) {
    const menu = dropdown.querySelector(menuSelector);
    if (!menu) return;

    const menuRect = menu.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 16;

    let delta = 0;
    if (menuRect.top < margin) {
      delta = menuRect.top - margin;
    } else if (menuRect.bottom > viewportHeight - margin) {
      delta = menuRect.bottom - (viewportHeight - margin);
    }

    if (Math.abs(delta) > 2) {
      window.scrollBy({
        top: delta,
        behavior: 'smooth',
      });
    }
  }

  function scheduleSmoothKeepInView(dropdown) {
    // Run once at animation start and once mid-transition so long menus stay visible.
    requestAnimationFrame(() => {
      smoothKeepMenuInView(dropdown);
      setTimeout(() => smoothKeepMenuInView(dropdown), 220);
    });
  }

  function expandMore(dropdown, moreAction) {
    const menu = dropdown.querySelector(menuSelector);
    if (!menu) return;

    const hiddenItems = menu.querySelectorAll('.dropdown__item--hidden');
    hiddenItems.forEach((item) => {
      item.classList.remove('dropdown__item--hidden');
      item.hidden = false;
    });

    moreAction.hidden = !hasHiddenMoreItems(dropdown);
    const button = dropdown.querySelector('.dropdown__button');
    if (button) button.focus({ preventScroll: true });
    updateDropdownPosition(dropdown);
    scheduleSmoothKeepInView(dropdown);
  }

  dropdowns.forEach((dropdown) => {
    const button = dropdown.querySelector('.dropdown__button');
    const moreAction = dropdown.querySelector('[data-action="show-more"]');
    if (moreAction) {
      moreAction.setAttribute('tabindex', '0');
      moreAction.setAttribute('role', 'button');
      moreAction.hidden = !hasHiddenMoreItems(dropdown);
    }

    dropdown.addEventListener('focusin', () => {
      updateDropdownPosition(dropdown);
      if (button && dropdown.contains(document.activeElement)) {
        scheduleSmoothKeepInView(dropdown);
      }
    });

    dropdown.addEventListener('mouseenter', () => {
      updateDropdownPosition(dropdown);
      scheduleSmoothKeepInView(dropdown);
    });

    if (button) {
      button.addEventListener('pointerdown', () => {
        requestAnimationFrame(() => {
          updateDropdownPosition(dropdown);
          scheduleSmoothKeepInView(dropdown);
        });
      });

      button.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        requestAnimationFrame(() => {
          updateDropdownPosition(dropdown);
          scheduleSmoothKeepInView(dropdown);
        });
      });
    }

    dropdown.addEventListener('pointerdown', (event) => {
      const moreTarget = event.target.closest('[data-action="show-more"]');
      if (!moreTarget || !dropdown.contains(moreTarget)) return;
      event.preventDefault();
      event.stopPropagation();
      expandMore(dropdown, moreTarget);
    });

    dropdown.addEventListener('keydown', (event) => {
      const moreTarget = event.target.closest('[data-action="show-more"]');
      if (!moreTarget || !dropdown.contains(moreTarget)) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      expandMore(dropdown, moreTarget);
    });
  });

  window.addEventListener('resize', updateAll);
  window.addEventListener('scroll', updateAll, { passive: true });

  updateAll();
}

function autoInitDropdownAutoPosition() {
  if (window.__faDropdownPositionInit) return;
  window.__faDropdownPositionInit = true;
  initDropdownAutoPosition();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitDropdownAutoPosition);
} else {
  autoInitDropdownAutoPosition();
}

export { initDropdownAutoPosition };
